<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { get_local_ip, real_id } from '../../../helpers/interface_helpers';
	import { getSensorAutomationOptions } from '../../../helpers/ext_sensor_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { valueOutOfRange } from '../../../helpers/utilities';
	import { getNumberInputConfig } from '../../../helpers/number_input_helpers';
	//Services
	import { localReq, postReq } from '../../../services/http';
	//Stores
	//Design system components
	import { Select, Input, Button, Text } from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../types/Device';

	export let device: Device;

	let waitForResponse = false;

	const id = real_id(device.id),
		adc_settings: {
			over_act: string;
			over_threshold: number;
			under_act: string;
			under_threshold: number;
		} = readDeviceSettings(device.id).adcs[0].relay_actions[device.channel],
		min = -10000,
		max = 100000,
		options = getSensorAutomationOptions(),
		setAdcAutomation = async () => {
			let res;
			waitForResponse = true;
			adc_settings.over_threshold = Number(adc_settings.over_threshold);
			adc_settings.under_threshold = Number(adc_settings.under_threshold);
			const local_ip = get_local_ip(id),
				data = {
					...adc_settings,
					relay_id: device.channel,
					sensor_id: 0,
				};
			if (local_ip) {
				const prefix = device.channels_count > 1 ? `relay${device.channel}_` : '';
				res = await localReq(
					device,
					`${local_ip}/settings/adc/0`,
					'post',
					{
						[`${prefix}over_act`]: data.over_act,
						[`${prefix}under_act`]: data.under_act,
						[`${prefix}over_threshold`]: data.over_threshold,
						[`${prefix}under_threshold`]: data.under_threshold,
					},
					6000,
				);
			} else {
				res = await postReq(`/device/generic_command`, {
					id,
					command: 'adc_actions',
					payload: JSON.stringify(data),
				});
			}
			const toastTexts = {
				success: $format('adc_saved_scc_toast', {
					default: 'ADC automation successfully saved.',
				}),
			};
			onSettingsResponse(res, device, toastTexts);
			waitForResponse = false;
		};
</script>

<Text styleType="body-small" class="mt-s mb-s"
	>{$format('adc_authomation_over_threshold', {
		default: 'Over-ADC threshold (mV):',
	})}
</Text>
<Input
	aria-label={$format('input_over_adc_threshold', {
		default: 'Input Over-ADC threshold',
	})}
	{...getNumberInputConfig()}
	size="m"
	value={String(adc_settings.over_threshold)}
	icon="fas fa-calculator"
	error={valueOutOfRange(adc_settings.over_threshold, min, max)
		? $format('min_max_error_description', {
				values: { min, max },
				default: 'Value must be between {min} and {max}.',
			})
		: ''}
	on:input={(e) => {
		adc_settings.over_threshold = e.target['value'];
	}}
	{min}
	{max}
	id="input-wedovas"
/>
<Text styleType="body-small" class="mt-s mb-s"
	>{$format('adc_authomation_over_action', {
		default: 'Over-ADC action:',
	})}
</Text>
<Select
	aria-label={$format('select_over_adc_action', {
		default: 'Select Over-ADC action',
	})}
	size="m"
	value={options.find((i) => i.value == adc_settings.over_act)}
	icon="fas fa-level-up-alt"
	{options}
	on:change={(e) => {
		adc_settings.over_act = e.detail['value'];
	}}
	id="select-daki0fo"
/>
<Text styleType="body-small" class="mt-s mb-s">
	{$format('adc_authomation_under_threshold', {
		default: 'Under-ADC threshold (mV):',
	})}
</Text>
<Input
	aria-label={$format('input_under_adc_threshold', {
		default: 'Input Under-ADC threshold',
	})}
	{...getNumberInputConfig()}
	size="m"
	value={String(adc_settings.under_threshold)}
	icon="fas fa-calculator"
	error={valueOutOfRange(adc_settings.under_threshold, min, max)
		? $format('min_max_error_description', {
				values: { min, max },
				default: 'Value must be between {min} and {max}.',
			})
		: ''}
	on:input={(e) => {
		adc_settings.under_threshold = e.target['value'];
	}}
	{min}
	{max}
	id="input-wh71x3a"
/>
<Text styleType="body-small" class="mt-s mb-s">
	{$format('adc_authomation_under_action', {
		default: 'Under-ADC action:',
	})}
</Text>
<Select
	aria-label={$format('select_under_adc_action', {
		default: 'Select Under-ADC action',
	})}
	size="m"
	class="mb-s"
	value={options.find((i) => i.value == adc_settings.under_act)}
	icon="fas fa-level-down-alt"
	{options}
	on:change={(e) => {
		adc_settings.under_act = e.detail['value'];
	}}
	id="select-2cgi6qo"
/>
<Button
	size="m"
	width="full"
	loading={waitForResponse}
	disabled={valueOutOfRange(adc_settings.over_threshold, min, max) ||
		valueOutOfRange(adc_settings.under_threshold, min, max)}
	on:click={setAdcAutomation}>{$format('button_save', { default: 'Save' })}</Button
>
